import { apiFetch } from "@/shared/api/http";

export type NotificationPermissionState = NotificationPermission | "unsupported";

export interface GamificacaoNotificationPreferences {
  conquistaDesbloqueada: boolean;
  desafioQuaseConcluido: boolean;
  temporadaEncerrando: boolean;
  recompensaDisponivel: boolean;
}

export function getNotificationPermission(): NotificationPermissionState {
  if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) return "unsupported";
  return Notification.permission;
}


export interface PushClientState {
  supported: boolean;
  permission: NotificationPermissionState;
  subscribed: boolean;
}

export async function getPushClientState(): Promise<PushClientState> {
  const permission = getNotificationPermission();
  if (permission === "unsupported") return { supported: false, permission, subscribed: false };
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    return { supported: true, permission, subscribed: !!subscription };
  } catch { return { supported: false, permission, subscribed: false }; }
}

export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (getNotificationPermission() === "unsupported") return "unsupported";
  return Notification.requestPermission();
}

export async function registerAppServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" });
  await registration.update().catch(() => undefined);
  if (registration.waiting) registration.waiting.postMessage({ type: "SKIP_WAITING" });
  registration.addEventListener("updatefound", () => {
    const worker = registration.installing;
    worker?.addEventListener("statechange", () => {
      if (worker.state === "installed" && navigator.serviceWorker.controller) worker.postMessage({ type: "SKIP_WAITING" });
    });
  });
  return registration;
}

export async function subscribeToPush(vapidPublicKey: string, endpoint = "/api/app/gamificacao/notificacoes/assinaturas") {
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const subscription = existing ?? await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });

  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) throw new Error("O navegador não retornou uma assinatura push válida.");

  try {
    await apiFetch<void>(endpoint, {
      method: "POST",
      body: JSON.stringify({
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
        dispositivo: navigator.userAgent.slice(0, 160),
      }),
    });
  } catch (error) {
    if (!existing) await subscription.unsubscribe().catch(() => undefined);
    throw error;
  }

  return subscription;
}

export async function unsubscribeFromPush(endpoint = "/api/app/gamificacao/notificacoes/assinaturas") {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  await apiFetch<void>(`${endpoint}?endpoint=${encodeURIComponent(subscription.endpoint)}`, { method: "DELETE" });
  await subscription.unsubscribe();
}

export async function getGamificacaoNotificationPreferences() {
  return apiFetch<GamificacaoNotificationPreferences>("/api/app/gamificacao/notificacoes/preferencias");
}

export async function updateGamificacaoNotificationPreferences(preferences: GamificacaoNotificationPreferences) {
  return apiFetch<void>("/api/app/gamificacao/notificacoes/preferencias", {
    method: "PUT",
    body: JSON.stringify(preferences),
  });
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export type GamificacaoNotificationType =
  | "ConquistaDesbloqueada"
  | "DesafioQuaseConcluido"
  | "TemporadaEncerrando"
  | "RecompensaDisponivel"
  | string;

export interface GamificacaoInboxItem {
  id: string;
  tipo: GamificacaoNotificationType;
  titulo: string;
  mensagem: string;
  url?: string | null;
  lida: boolean;
  criadaEmUtc: string;
}

export interface GamificacaoInbox {
  naoLidas: number;
  itens: GamificacaoInboxItem[];
}

export function getGamificacaoNotifications(limite = 50) {
  return apiFetch<GamificacaoInbox>(`/api/app/gamificacao/notificacoes?limite=${limite}`);
}

export function markGamificacaoNotificationRead(id: string) {
  return apiFetch<void>(`/api/app/gamificacao/notificacoes/${id}/lida`, { method: "PATCH" });
}

export function markAllGamificacaoNotificationsRead() {
  return apiFetch<void>("/api/app/gamificacao/notificacoes/lidas", { method: "PATCH" });
}
